import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { ApiError } from "@njbsoft/api-client";
import { BREAKPOINT_SM_QUERY, useMediaQuery } from "@njbsoft/form-ui";
import { type components } from "@njbsoft/types/field";
import { DataTable, EmptyState, PageHeader, Pagination, SkeletonTable } from "@/components/ui";
import { FieldAccessEnded } from "@/components/tester/chrome/field-access-ended";
import {
  buildLinkedAccountPremiseColumns,
  linkedPremiseSortBy,
} from "@/components/tester/work/linked-account-premise-columns";
import {
  emptyLinkedPremiseFilters,
  LinkedAccountPremiseFilters,
  type LinkedPremiseFilterState,
} from "@/components/tester/work/linked-account-premise-filters";
import { LinkedAccountGone } from "@/components/tester/work/linked-account-gone";
import { useLinkedAccount } from "@/hooks/use-linked-accounts";
import {
  useLinkedAccountPremises,
  type LinkedAccountPremisesParams,
} from "@/hooks/use-linked-account-premises";

type LinkedAccountPremise = components["schemas"]["LinkedAccountPremise"];

export const Route = createFileRoute("/_company/my-work/accounts/$linkId/")({
  component: LinkedAccountPremisesPage,
});

const PAGE_SIZE = 20;
/** The read's own default order, so the first render asks for what it sorts by. */
const DEFAULT_SORTING: SortingState = [{ id: "name", desc: false }];

/**
 * One linked account's premises: every premise the account reaches, each with
 * how many of the account's assemblies there the tester could start right now.
 *
 * A list page in full — its own top filter bar (search over name and address),
 * a server-paged table and Pagination. Search, sort and page are all the
 * server's, assembled in ONE params object, so a sort by Claimable reorders
 * the link's whole premise set; any filter or sort change returns to the
 * first page. The URL keeps the account, so Back from the assignment a Start
 * opens returns here.
 *
 * The header names the account and its utility from the row My Work already
 * read, else from a re-read of the list. A link the tester may no longer act
 * on — removed, its code rotated or turned off, the account closed, no active
 * grant at the utility — is the API's one 404, read as "no longer active".
 * Online only: offline the shell holds this route behind its connection panel.
 */
function LinkedAccountPremisesPage() {
  const { linkId } = Route.useParams();
  const { t } = useTranslation(["field-work", "common"]);
  const navigate = useNavigate();

  const [filters, setFilters] = useState<LinkedPremiseFilterState>(emptyLinkedPremiseFilters);
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [offset, setOffset] = useState(0);

  // A new search or a new sort re-pages from the top. Carrying the offset over
  // would land the tester on page 3 of a result set that may only have one.
  useEffect(() => {
    setOffset(0);
  }, [filters, sorting]);

  const firstSort = sorting[0];
  const sortBy = (firstSort ? linkedPremiseSortBy(firstSort.id) : undefined) ?? "name";
  const sortDir = firstSort?.desc === true ? "desc" : "asc";

  const params = useMemo<LinkedAccountPremisesParams>(
    () => ({
      search: filters.search.trim(),
      sortBy,
      sortDir,
      limit: PAGE_SIZE,
      offset,
    }),
    [filters, sortBy, sortDir, offset],
  );

  const premisesQuery = useLinkedAccountPremises(linkId, params);
  const account = useLinkedAccount(linkId).data ?? null;

  // Below sm the row is not enough of a target: a named Open leads it.
  const fromSm = useMediaQuery(BREAKPOINT_SM_QUERY, true);
  const openPremise = useCallback(
    (row: LinkedAccountPremise) => {
      void navigate({
        to: "/my-work/accounts/$linkId/premises/$facilityId",
        params: { linkId, facilityId: row.facility_id },
      });
    },
    [navigate, linkId],
  );
  const columns = useMemo(
    () => buildLinkedAccountPremiseColumns(t, { leadingOpen: fromSm ? undefined : openPremise }),
    [t, fromSm, openPremise],
  );

  const error = premisesQuery.error;
  if (error instanceof ApiError && error.status === 403) return <FieldAccessEnded />;

  const header = (
    <PageHeader
      title={account?.account_name ?? t("field-work:myWork.linkedAccounts.accountFallbackTitle")}
      subtitle={account?.org_name}
      backTo="/my-work"
      backLabel={t("field-work:myWork.linkedAccounts.backToMyWork")}
    />
  );

  if (error instanceof ApiError && error.status === 404) {
    return (
      <div className="space-y-6 p-4 sm:p-8">
        {header}
        <LinkedAccountGone />
      </div>
    );
  }

  const premises = premisesQuery.data?.items ?? [];
  const total = premisesQuery.data?.total ?? 0;
  const isFiltered = filters.search.trim() !== "";

  return (
    <div className="space-y-6 p-4 sm:p-8">
      {header}

      <LinkedAccountPremiseFilters value={filters} onChange={setFilters} />

      {premisesQuery.isError ? (
        <div className="rounded-md bg-danger/10 p-4 text-sm text-danger">
          {t("common:errors.somethingWentWrong")}
        </div>
      ) : premisesQuery.isLoading ? (
        <SkeletonTable columns={columns.length} />
      ) : premises.length === 0 ? (
        isFiltered ? (
          <EmptyState
            title={t("field-work:myWork.linkedAccounts.premises.emptyFiltered.title")}
            description={t("field-work:myWork.linkedAccounts.premises.emptyFiltered.description")}
            actionLabel={t("field-work:myWork.linkedAccounts.premises.filters.reset")}
            onAction={() => setFilters(emptyLinkedPremiseFilters)}
          />
        ) : (
          <EmptyState
            title={t("field-work:myWork.linkedAccounts.premises.empty.title")}
            description={t("field-work:myWork.linkedAccounts.premises.empty.description")}
          />
        )
      ) : (
        <>
          <DataTable
            columns={columns}
            data={premises}
            sorting={sorting}
            onSortingChange={setSorting}
            manualSorting
            onRowClick={openPremise}
          />
          <Pagination
            currentPage={Math.floor(offset / PAGE_SIZE) + 1}
            totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={(page) => setOffset((page - 1) * PAGE_SIZE)}
          />
        </>
      )}
    </div>
  );
}
